import React, { createContext, useContext, useState, useCallback } from 'react';

type SkeletonVariant = 
  | 'bible-verse-list'
  | 'bible-chapter-view'
  | 'prayers-grid'
  | 'prayers-detail'
  | 'profile-overview'
  | 'profile-settings';

interface SkeletonState {
  variant: SkeletonVariant;
  isVisible: boolean;
  animationDelay?: number;
}

interface SkeletonContextType {
  skeletons: Record<string, SkeletonState>;
  showSkeleton: (key: string, variant: SkeletonVariant, animationDelay?: number) => void;
  hideSkeleton: (key: string) => void;
  hideAllSkeletons: () => void;
  isSkeletonVisible: (key: string) => boolean;
}

const SkeletonContext = createContext<SkeletonContextType | undefined>(undefined);

export const SkeletonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [skeletons, setSkeletons] = useState<Record<string, SkeletonState>>({});

  const showSkeleton = useCallback((key: string, variant: SkeletonVariant, animationDelay = 0) => {
    setSkeletons(prev => ({
      ...prev,
      [key]: { variant, isVisible: true, animationDelay }
    }));
  }, []);

  const hideSkeleton = useCallback((key: string) => {
    setSkeletons(prev => {
      const newSkeletons = { ...prev };
      delete newSkeletons[key];
      return newSkeletons;
    });
  }, []);

  const hideAllSkeletons = useCallback(() => {
    setSkeletons({});
  }, []);

  const isSkeletonVisible = useCallback((key: string) => {
    return skeletons[key]?.isVisible || false;
  }, [skeletons]);

  return (
    <SkeletonContext.Provider value={{
      skeletons,
      showSkeleton,
      hideSkeleton,
      hideAllSkeletons,
      isSkeletonVisible
    }}>
      {children}
    </SkeletonContext.Provider>
  );
};

export const useSkeleton = () => {
  const context = useContext(SkeletonContext);
  if (!context) {
    throw new Error('useSkeleton must be used within SkeletonProvider');
  }
  return context;
};